<template>
  <div class="view settings">
    <div class="header-search">
      <div class="content title">{{ $t('headerSearchAddCompany') }}</div>
      <div class="content header-content">
        <div class="input-container">
          <input
            v-model="inputSearchCompany"
            type="text"
            :placeholder="$t('companySearchPlaceholder')"
            class="input"
            @keydown.enter="() => getCompany(inputSearchCompany)"
          />
          <div class="icon-container">
            <IconSearch class="icon-search" @click="() => getCompany(inputSearchCompany)" />
          </div>
        </div>

        <div v-if="user.isMainUser" class="main-button hide-medium" @click="$router.push('/importCompanies')">
          <IconImport class="icon icon-import"></IconImport>{{ $t('import') }}
        </div>
      </div>
    </div>
    <div v-if="loaded" class="content">
      <div class="header-mobile show-medium">
        <div class="filter-expander" @click="openFilter">
          {{ $t('filters') }}
          <div class="expand-container">
            <div class="expand"></div>
          </div>
        </div>
        <div class="link-button" @click="resetFilters">
          {{ $t('resetFilters') }}
        </div>
        <div class="main-button hide-medium" @click="exportWallet">
          {{ !loadingPortfolio ? $t('exportWallet') : '' }}
          <div v-if="loadingPortfolio" class="lds-ellipsis">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      </div>

      <div
        class="header no-scroll-bar"
        :class="{
          'hide-mobile': !showFilter,
        }"
      >
        <div class="filter-header show-medium">
          <div class="title">
            {{ $t('filters') }}
          </div>
          <div class="close-button cross inverse" @click="closeFilter"></div>
        </div>

        <div class="filter-footer show-medium">
          <div class="main-button" @click="closeFilter">
            {{ $t('search') }}
          </div>
        </div>

        <div class="container filters">
          <div class="form-container">
            <div class="input-title">{{ $t('monitoring') }}</div>
            <Dropdown
              class="priority-dropdown monitoring"
              :config="{
                inputSize: 48,
                active: false,
                showActivatedOnly: true,
              }"
              :values="packages"
              :placeholder="$t('chooseMonitoringLevel')"
              :on-change="value => (monitoringLevel = value.publicId)"
              :value="monitoringLevel"
            >
              <template #placeholder="{ value, isPlaceholder }">
                <MonitoringType v-if="!isPlaceholder" :config="{ maxElementWidth: 140 }" :level="getMonitoringInfos.level" :label="getMonitoringInfos.label" />
                <div v-else class="value grey">{{ value }}</div>
              </template>
              <template #choices="{ choice }">
                <MonitoringType
                  :config="{ maxElementWidth: 140 }"
                  :class="{ highlight: choice === monitoringLevel }"
                  :level="choice.level"
                  :label="choice.label"
                />
              </template>
            </Dropdown>
          </div>
          <div class="form-container">
            <div class="input-title">{{ $t('manager') }}</div>
            <Dropdown
              class="monitoring-dropdown"
              :config="{
                inputSize: 48,
                color: 'white',
                active: false,
                showActivatedOnly: true,
              }"
              :values="users"
              :placeholder="$t('chooseManager')"
              :on-change="updateManager"
              :value="manager"
            >
              <template #placeholder="{ value, isPlaceholder }">
                <div v-if="!isPlaceholder" class="manager">{{ getManagerInfos.firstName }} {{ getManagerInfos.lastName }}</div>
                <div v-else class="manager grey">{{ value }}</div>
              </template>
              <template #choices="{ choice }">
                <div class="manager white" :class="{ highlight: choice === manager }">{{ choice.firstName }} {{ choice.lastName }}</div>
              </template>
            </Dropdown>
          </div>
          <div class="form-container search">
            <div class="input-title">{{ $t('research') }}</div>
            <input v-model="search" type="text" class="input search" :placeholder="$t('companiesSearch')" />
          </div>
          <div class="form-container search">
            <div class="input-title">{{ $t('nextRenewalDate') }} de début</div>
            <input v-model="startRenewalDate" type="date" class="input search" :placeholder="$t('nextRenewalDate')" />
          </div>
          <div class="form-container search">
            <div class="input-title">{{ $t('nextRenewalDate') }} de fin</div>
            <input v-model="endRenewalDate" type="date" class="input search" :placeholder="$t('nextRenewalDate')" />
          </div>
          <div class="form-container">
            <div class="input-title">{{ $t('paginationNumberTitle') }}</div>
            <Dropdown
              class=""
              :config="{ inputSize: 48, active: false }"
              :values="[10, 20, 30, 40, 50, 60, 70, 80, 90, 100]"
              :placeholder="$t('choosePaginationNumber')"
              :on-change="value => (paginationNumber = value)"
              :value="paginationNumber"
            >
              <template #placeholder="{ value, isPlaceholder }">
                <div v-if="!isPlaceholder" class="value">{{ value }}</div>
                <div v-else class="value grey">{{ value }}</div>
              </template>
              <template #choices="{ choice }">
                <div class="value black">{{ choice }}</div>
              </template>
            </Dropdown>
          </div>
        </div>
      </div>

      <div class="header hide-medium">
        <div class="link-button reset-button" @click="resetFilters">{{ $t('resetFilters') }} <ResetIcon class="icon-reset" /></div>
        <div class="main-button hide-medium" @click="exportWallet">
          {{ !loadingPortfolio ? $t('exportWallet') : '' }}
          <div v-if="loadingPortfolio" class="lds-ellipsis">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      </div>

      <div class="main-button export-button show-small" @click="exportWallet">
        {{ !loadingPortfolio ? $t('exportWallet') : '' }}
        <div v-if="loadingPortfolio" class="lds-ellipsis">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div>

      <WalletTable :filters="getFilters" :users="users" :packages="packages" :config="{ pagination: true }" />
    </div>
    <div v-else class="content sub line">
      <div class="lds-ring">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
      {{ !loaded ? $t('searchInProgress') : $t('noCompanies') }}
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Dropdown from '@/components/Dropdown';
import MonitoringType from '@/components/monitoringType';
import WalletTable from '@/components/tables/WalletTable';

export default {
  components: { WalletTable, Dropdown, MonitoringType },

  data() {
    return {
      inputSearchCompany: '',
      monitoringTypes: '',
      packages: [],
      users: [],
      manager: '',
      monitoringLevel: '',
      paginationNumber: 10,
      loaded: false,
      search: '',
      showFilter: false,
      loadingPortfolio: false,
      startRenewalDate: null,
      endRenewalDate: null,
    };
  },

  computed: {
    getFilters() {
      return {
        isFiltered:
          (this.getMonitoringInfos && this.getMonitoringInfos.label) ||
          (this.getManagerInfos && this.getManagerInfos.lastName) ||
          this.search ||
          this.startRenewalDate ||
          this.endRenewalDate,
        manager: this.manager,
        monitoring: this.monitoringLevel,
        search: this.search,
        paginationNumber: this.paginationNumber,
        startRenewalDate: this.startRenewalDate,
        endRenewalDate: this.endRenewalDate,
      };
    },

    ...mapGetters(['isLogged', 'user', 'managerFilter']),

    getMonitoringInfos() {
      return this.packages.find(value => value.publicId === this.monitoringLevel);
    },

    getManagerInfos() {
      return this.users.find(value => value.id === this.manager);
    },
  },

  async mounted() {
    this.packages = (await this.$store.dispatch('getPackagesLevel')).items;
    this.users = (await this.$store.dispatch('getUsers', { id: this.user.client.id })).users;

    if (window.innerWidth > 576) {
      this.manager = this.managerFilter || this.user.id;
    }

    this.loaded = true;
  },

  methods: {
    openFilter() {
      this.showFilter = true;
      document.querySelector('.topbar').style.display = 'none';
    },

    closeFilter() {
      this.showFilter = false;
      document.querySelector('.topbar').style.display = 'flex';
    },

    getCompany() {
      if (this.inputSearchCompany.trim().length === 0) return;

      const isSiren = Number(this.inputSearchCompany.replace(/\s/g, ''));
      if (isSiren && isSiren.toString().length >= 6) {
        this.inputSearchCompany = this.inputSearchCompany.replace(/\s/g, '');
      }

      this.$router.push({
        name: 'search',
        query: { q: this.inputSearchCompany },
      });
    },

    resetFilters() {
      this.monitoringLevel = '';
      this.search = '';
      this.manager = '';
      this.startRenewalDate = null;
      this.endRenewalDate = null;
      this.$store.dispatch('setManagerFilter', { id: null });
    },

    async exportWallet() {
      this.loadingPortfolio = true;
      const result = await this.$store.dispatch('exportPortfolio');
      const content = result.headers['content-disposition'];
      const filename = content
        .split(';')
        .find(n => n.includes('filename='))
        .replace('filename=', '');
      const url = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      const child = document.body.appendChild(link);
      link.click();
      child.remove();
      window.URL.revokeObjectURL(url);
      this.loadingPortfolio = false;
    },

    updateManager(value) {
      this.manager = value.id;
      this.$store.dispatch('setManagerFilter', { id: value.id });
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.export-button {
  margin: 12px 0 18px !important;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch !important;
  margin-bottom: 24px;
  justify-content: space-between !important;
  width: 100%;

  .input-title {
    margin-top: 12px !important;
  }

  :deep(.dropdown),
  .input.search {
    margin-bottom: 0 !important;
  }

  :deep(.dropdown-container) {
    height: auto !important;
  }
}

.view {
  padding: 144px 0 64px;
  box-sizing: border-box;

  .header-search {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    padding: 128px 0 50px;
    background: colors.$main-color;
    margin-top: -144px;
    margin-bottom: 32px;

    .header-content {
      display: flex;
    }

    .title {
      width: 100%;
      font-weight: 500;
      font-size: 20px;
      text-align: left;
      color: colors.$white-color;
      display: flex;
      justify-content: flex-start;
      margin: 0 0 16px;
    }

    .input-container {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-grow: 1;
      margin: 0 32px 0 0;
      box-sizing: border-box;

      .icon-container {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 46px;
        padding: 0;
        color: colors.$lightblue-color;
        border-radius: 4px;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        border: solid 1px colors.$lightgrey-color;
        background: colors.$white-color;
        border-left: none;

        &:hover {
          cursor: pointer;

          .icon-search {
            transform: scale(1.1);
          }
        }
      }

      .input {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        border-right: none;
        padding-right: 0;
        margin: 0;
        text-overflow: ellipsis;
      }
    }

    .main-button {
      background: colors.$lightgreen-color;
      min-width: 48px;
      padding: 0 18px;
      border-radius: 5px;

      .icon {
        margin-right: 6px;
      }
    }
  }

  .content {
    &.sub {
      display: flex;
      padding: 80px 0 0;
      flex-direction: column;
      flex-shrink: 0;
      height: 200px;

      &.line {
        flex-direction: row;
        justify-content: center;
        align-items: center;
        color: colors.$main-color;
        flex-shrink: 0;

        .lds-ring {
          margin-right: 24px;
        }
      }

      .margin-link {
        margin: 80px 0 0;
      }
    }
    .input {
      width: 100%;
      height: 48px;
      margin: 0 0 16px;
      padding: 12px 16px;
      box-sizing: border-box;
      color: colors.$text-color;
      border: solid 1px colors.$lightgrey-color;
      border-radius: 4px;
      background: colors.$white-color;

      &::placeholder {
        color: colors.$lightgrey-color;
      }

      &.incorrect {
        border-color: colors.$error-color;
      }
    }

    .form-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
      width: calc(100% / 6.5);

      .input-title {
        width: 100%;
        color: colors.$darkgrey-color;
        font-weight: 500;
        font-size: 12px;
        margin: 24px 0 10px 0;
      }

      .priority-dropdown {
        .priority-level {
          display: flex;
          align-items: center;

          &:not(:nth-child(2n)) {
            flex-shrink: 0;
            width: 17px;
            height: 17px;
            background: colors.$red-color;
            color: colors.$white-color;
            border-radius: 50%;
            font-size: 12px;
            margin: 0 8px 0 0;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }

        .value {
          &.grey {
            color: colors.$lightgrey-color;
          }
        }
      }

      .monitoring-dropdown {
        .manager {
          max-width: 200px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;

          &.grey {
            color: colors.$lightgrey-color;
          }
        }
      }
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .container {
        display: flex;
        align-items: center;
        justify-content: flex-start;
      }

      .link-button {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        height: 48px;
        color: colors.$lightgrey-color;
      }

      .main-button {
        background: colors.$main-color;
        margin: 0 0 0 auto;
      }
    }
  }
}

.header-mobile {
  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: space-between;

  margin-bottom: 4px;

  .filter-expander {
    display: flex;
    flex-direction: row;

    align-items: center;
  }

  .expand-container {
    width: 34px;
    height: 34px;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: all 0.2s;

    &:hover {
      cursor: pointer;

      .expand {
        transform: rotate(45deg) scale(1.15);
      }
    }

    .expand {
      border: 2px solid colors.$text-color;
      border-left: none;
      border-top: none;
      width: 7px;
      height: 7px;
      transform: rotate(45deg);
      transition: all 0.2s;
    }

    &.active {
      .expand {
        transform: rotate(225deg);
      }
    }
  }
}

@media only screen and (max-width: responsive.$layout-breakpoint-small) {
  .view .header-search {
    padding-bottom: 32px;
  }

  .filter-header {
    margin-top: -84px;
    width: 100%;
    height: 64px;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    border-bottom: 1px solid #e1e5ea;

    .title {
      font-weight: 500;
      font-size: 20px;
      line-height: 23px;
      color: #1c236b;
    }
  }

  .filter-footer {
    position: absolute;
    bottom: 82px;
    height: 64px;
    padding: 12px 0;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    background: #ffffff;
    box-shadow: 0 0 20px rgba(210, 218, 228, 0.7);

    .main-button {
      background: colors.$green-color !important;
    }
  }

  .content {
    width: 100%;
    overflow: hidden;

    &.header-content {
      .input-container {
        margin-right: 0;

        .input {
          text-overflow: ellipsis !important;
        }
      }

      .main-button {
        display: none;
      }
    }

    .header {
      position: fixed;
      top: 0;
      z-index: 100000;
      background: white;
      padding-top: 80px;
      left: 0;
      right: 0;
      height: 100vh;
      flex-direction: column !important;
      padding-left: 16px;
      padding-right: 16px;
      align-items: end;
      justify-content: flex-start !important;

      .container {
        flex-direction: column !important;
        width: 100% !important;
      }

      &.hide-mobile {
        display: none;
      }
    }

    .form-container {
      width: 100%;
      margin-right: 0 !important;

      &.search {
        flex-grow: 0 !important;
        width: 100% !important;
      }

      .input-title {
        margin-top: 10px !important;
      }
    }

    .dropdown-container {
      width: 100% !important;
    }
  }
}
</style>
